/**
 * @fileoverview The plant card's note for a plant with no week (B136): a plant at Planning ("Planning · not planted yet"), which
 * carries the card's one-tap "Plant now" button (PlantNowButton, E5), or a plant in a sheltered bed ("Sheltered · weather
 * won't reach it"). The weather caption (WeekCaption) is for plants that have a week; these two have none, so this quiet line
 * says why, as the old week bar did. A plant with no week for another reason (the brief not in yet, no forecast) gets no line.
 *
 * It is a note, not a control: no tint, no chevron. Whether the plant is at Planning or sheltered is the server's (the brief's
 * stage; the bed's sheltered flag), and the app decides nothing.
 *
 * Where: the plant card (PlantCard), under the photo, where the caption would be.
 * API: none of its own (Plant now has PlantNowButton's).
 * Words (en/es): plants.card.noWeek.planning, plants.card.noWeek.sheltered.
 *
 * @module NoWeekNote
 */

import React from 'react';
import { CalendarDays, House } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';

/** What the note is given. */
interface NoWeekNoteProps {
  /** Why there is no week. */
  kind: 'planning' | 'sheltered';
  /** What follows the words at the right end (a Planning plant's "Plant now" button). */
  children?: React.ReactNode;
}

/**
 * The note: an icon and a line, with the Plant now button at the right end for a Planning plant.
 *
 * @param props - NoWeekNoteProps.
 */
export const NoWeekNote: React.FC<NoWeekNoteProps> = ({ kind, children }) => {
  const { reactTranslate: t } = useTranslation();
  const Icon = kind === 'planning' ? CalendarDays : House;
  return (
    <div className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-muted-foreground">
      <Icon className="h-[1.125rem] w-[1.125rem] shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 leading-snug">{t(`plants.card.noWeek.${kind}`)}</span>
      {children}
    </div>
  );
};
